/**
 * SpeedtestMonitorPage — Story 5.5 Speedtests Ergebnis-Monitor
 *
 * Referenz (AUTORITATIV): v2-final/speedtest.html — kompakte Tabelle
 * (Hotspot/Down/Up/Ping/Result) mit Breadcrumb "Hotspots > Speedtest".
 *
 * Design-Fix (Abnahme P1): vorheriges Hero-Card-Layout durch die Tabelle aus
 * der Referenz ersetzt.
 *
 * Issue #112: den "Run speedtest"-Button samt Modal ersatzlos entfernt — es
 * gibt keinen Trigger-Endpunkt im OpenAPI-Contract, Speedtests führt einzig
 * das Helium-Netzwerk autonom aus; der Button/das Modal waren reines
 * UI-Feedback ohne Backend-Wirkung und haben Nutzende in die Irre geführt.
 *
 * Golive-Anpassung #0409: die Jitter-Spalte wurde entfernt — avg_jitter_ms
 * ist laut OpenAPI dauerhaft null (dokumentierte Datenlücke, der Mobile-
 * Oracle-Report enthält keinen Jitter-Wert), die Spalte zeigte deshalb nie
 * mehr als einen Platzhalter.
 */
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from '@tanstack/react-router'
import { StatusChip } from '../../components/ui/StatusChip'
import { Pagination } from '../../components/Pagination'
import { hotspotDisplayName } from '../../lib/hotspotDisplayName'
import { activeBcp47 } from '../../lib/locale'
import { useSpeedtestMonitor, type SpeedtestMonitorEntry } from './useSpeedtestMonitor'
import {
  speedtestQuality,
  QUALITY_GOOD_MIN_DOWN_MBPS,
  QUALITY_WARNING_MIN_DOWN_MBPS,
} from './speedtestQuality'
import './speedtest.css'

// --- Formatierungs-Helfer ---------------------------------------------------

function fmtMbps(value: number | null | undefined): string | null {
  if (value === null || value === undefined) return null
  // Issue #55: hart 'en-US' verdrahtet, unabhängig von der aktiven App-Sprache.
  return value.toLocaleString(activeBcp47(), {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  })
}

function fmtMs(value: number | null | undefined): string | null {
  if (value === null || value === undefined) return null
  return Math.round(value).toString()
}

// --- Zeile -------------------------------------------------------------------

function SpeedtestRow({ entry }: { entry: SpeedtestMonitorEntry }) {
  const { t, i18n } = useTranslation('speedtest')

  const quality = speedtestQuality(entry.avg_down_mbps)
  const down = fmtMbps(entry.avg_down_mbps)
  const up = fmtMbps(entry.avg_up_mbps)
  const ping = fmtMs(entry.avg_ping_ms)
  const noValue = t('list.noValue')

  const takenAt = entry.last_taken_at
    ? t('list.lastTest', {
        date: new Intl.DateTimeFormat(i18n.language, {
          dateStyle: 'medium',
          timeStyle: 'short',
        }).format(new Date(entry.last_taken_at)),
      })
    : t('list.neverTested')

  const name = hotspotDisplayName(entry.name, entry.address, t('list.unknownHotspot'))

  const qualityTooltip =
    quality === 'good'
      ? t('quality.good.tooltip', { threshold: QUALITY_GOOD_MIN_DOWN_MBPS })
      : quality === 'warning'
        ? t('quality.warning.tooltip', {
            thresholdLow: QUALITY_WARNING_MIN_DOWN_MBPS,
            thresholdHigh: QUALITY_GOOD_MIN_DOWN_MBPS,
          })
        : t('quality.poor.tooltip', { threshold: QUALITY_WARNING_MIN_DOWN_MBPS })

  return (
    <div
      className="list-row st-row"
      role="listitem"
      data-testid={`speedtest-row-${entry.hotspot_id}`}
    >
      <div className="st-cell-identity">
        {entry.hotspot_id != null ? (
          <Link
            to="/hotspots/$id"
            params={{ id: String(entry.hotspot_id) }}
            className="st-identity-name"
          >
            {name}
          </Link>
        ) : (
          <span className="st-identity-name">{name}</span>
        )}
        <div className="st-identity-sub">
          <span>{takenAt}</span>
          {typeof entry.sample_count === 'number' && (
            <span>· {t('list.sampleCount', { count: entry.sample_count })}</span>
          )}
        </div>
      </div>

      <div className="num st-cell-metric">{down ?? noValue}</div>
      <div className="num st-cell-metric">{up ?? noValue}</div>
      <div className="num st-cell-metric st-col-md">{ping ?? noValue}</div>
      <div className="st-cell-result st-col-md">
        {quality !== null ? (
          <StatusChip
            kind="quality"
            status={quality}
            label={t(`quality.${quality}.label`)}
            tooltip={qualityTooltip}
            data-testid={`speedtest-quality-${entry.hotspot_id}`}
          />
        ) : (
          <span
            className="pill"
            data-testid={`speedtest-quality-${entry.hotspot_id}`}
            data-status="none"
            title={t('quality.none.tooltip')}
          >
            {t('quality.none.label')}
          </span>
        )}
      </div>
    </div>
  )
}

function ColumnHead() {
  const { t } = useTranslation('speedtest')
  return (
    <div className="list-row st-row st-head" aria-hidden="true">
      <div className="st-cell-identity">{t('list.colHotspot')}</div>
      <div className="num st-cell-metric">{t('list.colDown')}</div>
      <div className="num st-cell-metric">{t('list.colUp')}</div>
      <div className="num st-cell-metric st-col-md">{t('list.colPing')}</div>
      <div className="st-cell-result st-col-md">{t('list.colResult')}</div>
    </div>
  )
}

// --- Zustände ----------------------------------------------------------------

function LoadingState() {
  const { t } = useTranslation('speedtest')
  return (
    <div className="list" aria-busy="true" aria-label={t('loading')}>
      {Array.from({ length: 4 }).map((_, i) => (
        <div className="list-row skeleton" aria-hidden="true" key={i}>
          <div className="skeleton-line" style={{ width: 220, height: 16 }} />
          <div className="skeleton-line" style={{ width: 120, height: 16 }} />
        </div>
      ))}
    </div>
  )
}

function ErrorState({ onRetry }: { onRetry: () => void }) {
  const { t } = useTranslation('speedtest')
  return (
    <div className="card speedtest-empty" role="alert" data-testid="speedtest-error">
      <p>{t('error.load')}</p>
      <button type="button" className="btn btn-primary" style={{ marginTop: 12 }} onClick={onRetry}>
        {t('error.retry')}
      </button>
    </div>
  )
}

// Fallback, falls `meta.window_days` (noch) fehlt — muss mit
// Api::V2::HotspotsController::SPEEDTEST_MONITOR_WINDOW_DAYS übereinstimmen.
const DEFAULT_WINDOW_DAYS = 30

function EmptyState({ windowDays }: { windowDays: number }) {
  const { t } = useTranslation('speedtest')
  return (
    <div className="card speedtest-empty" data-testid="speedtest-empty">
      <p>{t('empty.title', { days: windowDays })}</p>
      <p className="speedtest-empty-hint">{t('empty.hint', { days: windowDays })}</p>
    </div>
  )
}

// --- Seite -------------------------------------------------------------------

export function SpeedtestMonitorPage() {
  const { t } = useTranslation('speedtest')
  const [page, setPage] = useState(1)
  const { entries, meta, isLoading, isError, refetch } = useSpeedtestMonitor(page)

  // W3-F6: Seite klemmen. Schrumpft die Ergebnismenge (z.B. Hotspot entfernt),
  // zeigt eine zu hohe Seitenzahl dauerhaft eine leere Liste ohne bedienbare
  // Pagination — daher zurück auf die letzte gültige Seite.
  const totalPages = meta?.total_pages ?? 1
  if (meta && totalPages >= 1 && page > totalPages) {
    setPage(totalPages)
  }

  const hasEntries = !isLoading && !isError && !!entries && entries.length > 0
  const isEmpty = !isLoading && !isError && !!entries && entries.length === 0

  return (
    <section data-testid="speedtest-page">
      <nav
        aria-label="Breadcrumb"
        style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, marginBottom: 8 }}
      >
        <span className="muted">{t('breadcrumb.hotspots')}</span>
        <span className="muted" aria-hidden="true">
          ›
        </span>
        <span style={{ fontWeight: 500 }}>{t('breadcrumb.current')}</span>
      </nav>

      <div
        style={{
          display: 'flex',
          alignItems: 'flex-end',
          justifyContent: 'space-between',
          gap: 16,
          flexWrap: 'wrap',
          marginBottom: 16,
        }}
      >
        <div>
          <h1 style={{ margin: '0 0 4px', fontSize: 22 }}>
            {t('title')}{' '}
            <span className="pill pill-primary" style={{ verticalAlign: 'middle' }}>
              {t('mobileBadge')}
            </span>
          </h1>
          <div className="muted" style={{ fontSize: 13 }}>
            {t('subtitle')}
          </div>
        </div>
      </div>

      {isLoading && <LoadingState />}
      {isError && <ErrorState onRetry={refetch} />}
      {isEmpty && <EmptyState windowDays={meta?.window_days ?? DEFAULT_WINDOW_DAYS} />}

      {hasEntries && (
        <>
          <div className="list" role="list" data-testid="speedtest-list">
            <ColumnHead />
            {entries.map((entry, i) => (
              <SpeedtestRow key={entry.hotspot_id ?? entry.address ?? i} entry={entry} />
            ))}
          </div>
          <Pagination
            page={meta?.current_page ?? page}
            pages={totalPages}
            count={meta?.total_count ?? entries.length}
            onPageChange={setPage}
          />
        </>
      )}
    </section>
  )
}
